import {
  type IFormJBPHPContactChangeRequest,
  JBPHPContactChangeRequestStatus,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'

export interface IPbmReplacementPerson {
  email: string
  firstName: string
  lastName: string
}

/** A PBM of a partner the manager asked to replace, while the change request is still open. */
export interface IPendingPbmReplacement {
  partnerId: string
  fromEmail: string
  /** Who takes the partner over; unknown where several requests are open at once. */
  toName?: string
}

export const isSameEmail = (left: string, right: string) =>
  left.trim().toLowerCase() === right.trim().toLowerCase()

/** `Anna Maria Ward` → `Anna Maria` and `Ward`; a name the payload does not hold falls back to the email. */
export const splitPersonName = (fullName: string, email: string): IPbmReplacementPerson => {
  const words = fullName.trim().split(/\s+/).filter(Boolean)

  if (words.length === 0) {
    return { email, firstName: email, lastName: '' }
  }

  return {
    email,
    firstName: words.length > 1 ? words.slice(0, -1).join(' ') : words[0],
    lastName: words.length > 1 ? words[words.length - 1] : '',
  }
}

const isOpenTicket = ({ TicketId, TicketStatus }: IFormJBPHPContactChangeRequest) =>
  TicketId > 0 && TicketStatus !== JBPHPContactChangeRequestStatus.Closed

/**
 * The replacement as the HP contacts flow states it: the current PBM leaves the partner and the new
 * one joins it on the same terms — business units, roles, lead flag and period — under the manager
 * who asked for it.
 */
export const buildPbmReplacementRequests = (
  current: IFormJBPHPContactChangeRequest,
  next: IPbmReplacementPerson,
  manager: IPbmReplacementPerson
): IFormJBPHPContactChangeRequest[] => {
  const ticket = {
    TicketId: -1,
    TicketStatus: JBPHPContactChangeRequestStatus.New,
  }

  return [
    { ...current, ...ticket, RequestAction: 'Delete' },
    {
      ...current,
      ...ticket,
      RequestAction: 'Add',
      Email: next.email,
      FirstName: next.firstName,
      LastName: next.lastName,
      ManagerEmail: manager.email,
      ManagerFirstName: manager.firstName,
      ManagerLastName: manager.lastName,
    },
  ]
}

/**
 * Replacements still waiting for approval among a partner's HP contacts. A removal pairs with the
 * addition only when it is the one open addition, since the requests carry no link to each other.
 */
export const getPendingPbmReplacements = (
  partnerId: string,
  contacts: IFormJBPHPContactChangeRequest[]
): IPendingPbmReplacement[] => {
  const open = contacts.filter(isOpenTicket)
  const removals = open.filter(({ RequestAction }) => RequestAction === 'Delete')
  const additions = open.filter(({ RequestAction }) => RequestAction === 'Add')
  const [addition] = additions.length === 1 && removals.length === 1 ? additions : []

  return removals.map(({ Email }) => ({
    partnerId,
    fromEmail: Email,
    toName: addition
      ? [addition.FirstName, addition.LastName].filter(Boolean).join(' ') || addition.Email
      : undefined,
  }))
}
